import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  format,
  parseISO,
  isValid,
  isBefore,
  startOfDay,
  isAfter,
  addYears,
  subYears,
  setMonth,
  setYear,
  startOfYear,
  endOfYear,
} from "date-fns";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { cn } from "@/utils/cn";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const YEARS_PER_PAGE = 12;

type PickerView = "days" | "months" | "years";

export interface DateInputProps {
  id?: string;
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  min?: string;
  max?: string;
  readOnly?: boolean;
  className?: string;
  isDateBlocked?: (date: Date) => boolean;
  getBlockedReason?: (date: Date) => string | undefined;
}

export function DateInput({
  id,
  value,
  onChange,
  min,
  max,
  readOnly,
  className,
  isDateBlocked,
  getBlockedReason,
}: DateInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<PickerView>("days");

  const selectedDate = useMemo(() => {
    if (!value) return null;
    const d = parseISO(value);
    return isValid(d) ? d : null;
  }, [value]);

  const [viewMonth, setViewMonth] = useState(
    () => selectedDate ?? new Date()
  );

  const today = useMemo(() => startOfDay(new Date()), []);

  const minDate = useMemo(() => {
    if (!min) return null;
    const d = parseISO(min);
    return isValid(d) ? startOfDay(d) : null;
  }, [min]);

  const maxDate = useMemo(() => {
    if (!max) return null;
    const d = parseISO(max);
    return isValid(d) ? startOfDay(d) : null;
  }, [max]);

  const displayValue = useMemo(
    () => (selectedDate ? format(selectedDate, "MM/dd/yyyy") : ""),
    [selectedDate]
  );

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth));
    const end = endOfWeek(endOfMonth(viewMonth));
    const days: Date[] = [];
    let current = start;
    while (!isAfter(current, end)) {
      days.push(current);
      current = addDays(current, 1);
    }
    return days;
  }, [viewMonth]);

  const dropdownPosition = useMemo(() => {
    if (!isOpen || !inputRef.current) return null;
    const rect = inputRef.current.getBoundingClientRect();
    return { top: rect.bottom + 4, left: rect.left };
  }, [isOpen]);

  const yearPageStart = useMemo(
    () =>
      Math.floor(viewMonth.getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE,
    [viewMonth]
  );

  const yearPage = useMemo(
    () =>
      Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i),
    [yearPageStart]
  );

  const isMonthDisabled = useCallback(
    (monthIndex: number) => {
      const candidate = setMonth(viewMonth, monthIndex);
      if (minDate && isBefore(endOfMonth(candidate), minDate)) return true;
      if (maxDate && isAfter(startOfMonth(candidate), maxDate)) return true;
      return false;
    },
    [viewMonth, minDate, maxDate]
  );

  const isYearDisabled = useCallback(
    (year: number) => {
      const candidate = setYear(viewMonth, year);
      if (minDate && isBefore(endOfYear(candidate), minDate)) return true;
      if (maxDate && isAfter(startOfYear(candidate), maxDate)) return true;
      return false;
    },
    [viewMonth, minDate, maxDate]
  );

  useEffect(() => {
    if (selectedDate) {
      setViewMonth(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  const isDayDisabled = useCallback(
    (day: Date) => {
      if (minDate && isBefore(day, minDate)) return true;
      if (maxDate && isAfter(day, maxDate)) return true;
      if (isDateBlocked?.(day)) return true;
      return false;
    },
    [minDate, maxDate, isDateBlocked]
  );

  const handleDateSelect = useCallback(
    (day: Date) => {
      if (isDayDisabled(day)) return;
      onChange({ target: { value: format(day, "yyyy-MM-dd") } });
      setIsOpen(false);
    },
    [isDayDisabled, onChange]
  );

  const toggleOpen = useCallback(() => {
    if (readOnly) return;
    setIsOpen((prev) => {
      if (prev) return false;
      setView("days");
      return true;
    });
  }, [readOnly]);

  const goPrevious = useCallback(() => {
    setViewMonth((m) =>
      view === "days"
        ? subMonths(m, 1)
        : subYears(m, view === "months" ? 1 : YEARS_PER_PAGE)
    );
  }, [view]);

  const goNext = useCallback(() => {
    setViewMonth((m) =>
      view === "days"
        ? addMonths(m, 1)
        : addYears(m, view === "months" ? 1 : YEARS_PER_PAGE)
    );
  }, [view]);

  const selectMonth = useCallback((monthIndex: number) => {
    setViewMonth((m) => setMonth(m, monthIndex));
    setView("days");
  }, []);

  const selectYear = useCallback((year: number) => {
    setViewMonth((m) => setYear(m, year));
    setView("months");
  }, []);

  const selectToday = useCallback(() => {
    const now = startOfDay(new Date());
    if (isDayDisabled(now)) return;
    onChange({ target: { value: format(now, "yyyy-MM-dd") } });
    setIsOpen(false);
  }, [isDayDisabled, onChange]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const parsed = parseISO(raw);
      if (isValid(parsed) && !isDayDisabled(parsed)) {
        onChange({ target: { value: format(parsed, "yyyy-MM-dd") } });
      }
    },
    [isDayDisabled, onChange]
  );

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center">
        <input
          ref={inputRef}
          id={id}
          type="text"
          readOnly
          value={displayValue}
          placeholder="mm/dd/yyyy"
          onClick={toggleOpen}
          onChange={handleInputChange}
          className={cn(
            "w-full rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400",
            !readOnly && "cursor-pointer",
            className
          )}
        />
        {!readOnly && (
          <button
            type="button"
            onClick={toggleOpen}
            className="-ml-8 flex items-center justify-center text-slate-400 hover:text-slate-600"
          >
            <Calendar className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="z-[100] w-[17rem] rounded-lg border border-slate-200 bg-white p-3 shadow-xl ring-1 ring-black/5"
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
          >
            <div className="mb-2 flex items-center justify-between gap-1">
              <button
                type="button"
                aria-label="Previous"
                onClick={goPrevious}
                className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() =>
                  setView((v) =>
                    v === "days" ? "months" : v === "months" ? "years" : "days"
                  )
                }
                className="flex-1 rounded-md px-2 py-1 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100"
              >
                {view === "days" && format(viewMonth, "MMMM yyyy")}
                {view === "months" && format(viewMonth, "yyyy")}
                {view === "years" &&
                  `${yearPageStart} – ${yearPageStart + YEARS_PER_PAGE - 1}`}
              </button>
              <button
                type="button"
                aria-label="Next"
                onClick={goNext}
                className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {view === "days" && (
              <>
                <div className="mb-1 grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {WEEKDAYS.map((d) => (
                    <div key={d} className="py-1">
                      {d}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-y-0.5">
                  {calendarDays.map((day) => {
                    if (!isSameMonth(day, viewMonth)) {
                      return (
                        <div
                          key={day.toISOString()}
                          className="invisible h-9 w-9"
                        />
                      );
                    }

                    const isSelected =
                      selectedDate && isSameDay(day, selectedDate);
                    const isToday = isSameDay(day, today);
                    const blocked = isDateBlocked?.(day);
                    const outOfRange =
                      (minDate && isBefore(day, minDate)) ||
                      (maxDate && isAfter(day, maxDate));
                    const disabled = blocked || outOfRange;

                    return (
                      <button
                        key={day.toISOString()}
                        type="button"
                        disabled={!!disabled}
                        title={blocked ? getBlockedReason?.(day) : undefined}
                        onClick={() => handleDateSelect(day)}
                        className={cn(
                          "mx-auto flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors",
                          !disabled &&
                            !isSelected &&
                            "cursor-pointer text-slate-700 hover:bg-slate-100",
                          isSelected &&
                            "bg-slate-900 font-semibold text-white hover:bg-slate-800",
                          isToday &&
                            !isSelected &&
                            !disabled &&
                            "font-semibold text-slate-900 ring-1 ring-inset ring-slate-300",
                          blocked &&
                            "cursor-not-allowed text-red-300 line-through",
                          outOfRange &&
                            !blocked &&
                            "cursor-not-allowed text-slate-300"
                        )}
                      >
                        {format(day, "d")}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {view === "months" && (
              <div className="grid grid-cols-3 gap-1.5 py-1">
                {MONTHS_SHORT.map((label, index) => {
                  const disabled = isMonthDisabled(index);
                  const isCurrent = viewMonth.getMonth() === index;

                  return (
                    <button
                      key={label}
                      type="button"
                      disabled={disabled}
                      onClick={() => selectMonth(index)}
                      className={cn(
                        "rounded-md py-2 text-sm transition-colors",
                        !disabled &&
                          !isCurrent &&
                          "cursor-pointer text-slate-700 hover:bg-slate-100",
                        isCurrent &&
                          !disabled &&
                          "bg-slate-900 font-semibold text-white hover:bg-slate-800",
                        disabled && "cursor-not-allowed text-slate-300"
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            {view === "years" && (
              <div className="grid grid-cols-3 gap-1.5 py-1">
                {yearPage.map((year) => {
                  const disabled = isYearDisabled(year);
                  const isCurrent = viewMonth.getFullYear() === year;

                  return (
                    <button
                      key={year}
                      type="button"
                      disabled={disabled}
                      onClick={() => selectYear(year)}
                      className={cn(
                        "rounded-md py-2 text-sm transition-colors",
                        !disabled &&
                          !isCurrent &&
                          "cursor-pointer text-slate-700 hover:bg-slate-100",
                        isCurrent &&
                          !disabled &&
                          "bg-slate-900 font-semibold text-white hover:bg-slate-800",
                        disabled && "cursor-not-allowed text-slate-300"
                      )}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-2 flex justify-end border-t border-slate-100 pt-2">
              <button
                type="button"
                onClick={selectToday}
                className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                Today
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
